import React from "react";
import { Link, useLocation } from "react-router-dom";
import { scrollToHash, splitHashTarget } from "../../utils/scrollToHash";

const variants = {
    primary: "bg-gradient-brand hover:bg-gradient-brand-hover text-white shadow-glow-purple-sm hover:shadow-glow-purple-md hover:-translate-y-0.5 active:translate-y-0",
    secondary: "bg-white/[0.05] hover:bg-white/[0.10] text-white border border-white/[0.12] hover:border-white/[0.22] hover:-translate-y-0.5 active:translate-y-0",
    outline: "bg-transparent hover:bg-brand-500/10 text-white border border-brand-500/40 hover:border-brand-400 hover:-translate-y-0.5 active:translate-y-0",
    ghost: "bg-transparent hover:bg-white/5 text-zinc-300 hover:text-white",
    whatsapp: "bg-gradient-brand hover:bg-gradient-brand-hover text-white shadow-glow-purple-sm hover:shadow-glow-purple-md hover:-translate-y-0.5 active:translate-y-0",
};

const sizes = {
    sm: "px-4 py-2 text-body-sm rounded-lg",
    md: "px-6 py-3 text-body-base rounded-xl",
    lg: "px-8 py-4 text-body-lg rounded-xl",
};

export default function Button({
    children,
    variant = "primary",
    size = "md",
    href,
    to,
    external,
    className = "",
    icon,
    iconPosition = "left",
    onClick,
    ...props
}) {
    const location = useLocation();
    const baseClasses = `inline-flex items-center justify-center gap-2 font-display font-semibold transition-all duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base ${variants[variant]} ${sizes[size]} ${className}`;

    // An anchor pointing at the page you are already on is rendered as a
    // native <a href="#section"> rather than a router Link. React Router does
    // not re-run location effects for a hash-only change, so a Link would
    // update the URL without ever scrolling. A native anchor scrolls reliably
    // (index.css sets scroll-behavior: smooth) and stays keyboard accessible.
    const { path: targetPath, hash: targetHash } = splitHashTarget(to);
    const isSamePageAnchor = Boolean(to) && Boolean(targetHash) && targetPath === location.pathname;

    const handleAnchorClick = (event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        // Also scroll programmatically, so the target receives focus for
        // keyboard and screen-reader users.
        scrollToHash(targetHash);
    };

    const content = (
        <>
            {icon && iconPosition === "left" && <span className="flex-shrink-0">{icon}</span>}
            {children}
            {icon && iconPosition === "right" && <span className="flex-shrink-0">{icon}</span>}
        </>
    );

    if (isSamePageAnchor) {
        return (
            <a href={targetHash} className={baseClasses} onClick={handleAnchorClick} {...props}>
                {content}
            </a>
        );
    }

    if (to) {
        return (
            <Link to={to} className={baseClasses} onClick={onClick} {...props}>
                {content}
            </Link>
        );
    }

    if (href) {
        return (
            <a
                href={href}
                className={baseClasses}
                onClick={onClick}
                {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                {...props}
            >
                {content}
            </a>
        );
    }

    return (
        <button className={baseClasses} onClick={onClick} {...props}>
            {content}
        </button>
    );
}
